<template>
  <ld-modal-content title="Feedback">
    <template #default>
      <div class="feedback-form">
        <template v-for="question in questions" :key="question.key">
          <input-with-validation
            v-if="question.type === 'radio'"
            :field-label="question.question"
            expanded
          >
            <template #custom>
              <div class="radio-group">
                <b-radio
                  v-for="option in question.options"
                  :key="option"
                  v-model="answers[question.key]"
                  :native-value="option"
                >
                  {{ option }}
                </b-radio>
              </div>
            </template>
          </input-with-validation>
          <input-with-validation
            v-else
            b-name="input"
            :field-label="question.question"
            type="textarea"
            v-model="answers[question.key]"
          />
        </template>
      </div>
    </template>
    <template #footer>
      <div class="action-buttons is-flex is-justify-content-center">
        <button class="button" @click="$emit('close')">Cancel</button>
        <button class="button is-success" @click="submit" :disabled="isSubmitting">Submit</button>
      </div>
    </template>
  </ld-modal-content>
</template>

<script>
import LdModalContent from '@/components/LdModalContent.vue'
import InputWithValidation from '@/components/validation/InputWithValidation.vue'
import to from '@/lib/to'

const YES_NO_OPTIONS = ['Yes', 'No', 'I am not sure']

const FEATURE_QUESTIONS = {
  crystal_ball: [
    { key: 'problem_area', question: 'What is the primary problem area?', type: 'radio', options: ['Missed something obvious', 'Claimed something that was not present', 'Something else'] },
    { key: 'occurrence', question: 'Is this issue a rare occurrence or do you see this problem regularly?', type: 'radio', options: ['Never seen it before', 'Rarely seen', 'Regularly seen'] },
    { key: 'additional_details', question: 'Additional details or feedback?', type: 'text' }
  ],
  pressure_risk: [
    { key: 'turning_event', question: 'Was there a turning event in the last hour?', type: 'radio', options: YES_NO_OPTIONS },
    { key: 'at_rest', question: 'Is the patient currently at rest?', type: 'radio', options: YES_NO_OPTIONS },
    { key: 'present_and_alone', question: 'Is the patient present and alone?', type: 'radio', options: YES_NO_OPTIONS },
    { key: 'additional_feedback', question: 'Any additional feedback?', type: 'text' }
  ]
}

export default {
  name: 'UserFeedbackModal',
  components: { LdModalContent, InputWithValidation },
  emits: ['close', 'submitted'],
  props: {
    feature: {
      type: String,
      required: true
    },
    metadata: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    questions() {
      return FEATURE_QUESTIONS[this.feature] || []
    }
  },
  methods: {
    async submit() {
      this.isSubmitting = true
      const feedback = this.questions
        .filter(q => this.answers[q.key])
        .map(q => ({ question: q.question, answer: this.answers[q.key] }))

      const [err] = await to(this.$http.post('/user_feedbacks', {
        feature: this.feature,
        sentiment: 'negative',
        feedback,
        metadata: this.metadata
      }, { skipLoading: true }))

      this.isSubmitting = false

      if (err) {
        this.$buefy.toast.open({
          message: 'Failed to submit feedback',
          type: 'is-danger',
          duration: 3000
        })
        return
      }

      this.$emit('submitted')
      this.$emit('close')
    }
  },
  data() {
    return {
      answers: {},
      isSubmitting: false
    }
  }
}
</script>

<style scoped lang="scss">
.feedback-form {
  min-width: 400px;
}
.radio-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.action-buttons {
  gap: 10px;
}
</style>
